import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {PrettyPrinter} from "../../../components/Utils/PrettyPrinter";
import {MarketingTabMapper} from "../utils/MarketingTabMapper";
import "./Chart.scss";
import {LabelOptions} from "./HorizontalGroupBarChart";

export interface BarChartRow {
    name: string;
    value: number;
    color: string;
}

interface BarChartProps {
    width: number;
    height: number;
    color: string;
    data: BarChartRow[];
    yUnitOfMeasure: string;
    minYScaleValue?: number;
    withYScaleValues?: boolean;
    withFrequencyField?: boolean;
    labelsOptions?: LabelOptions;
    selectedItem?: string;
    onPathOver?: Function;

}

interface BarChartState {
    selectedItem: string;
}

export class BarChart extends React.Component<BarChartProps, BarChartState> {

    private static DEFAULT_COLOR = "var(--vebox-gray-hover-light)";
    /** The color of histogram bars. */
    private static BAR_COLOR: string = "#75ca7e";

    constructor(props: BarChartProps) {
        super(props);
        this.state = {selectedItem: null};
        this.setSelectedItem = this.setSelectedItem.bind(this);
    }

    public setSelectedItem(itemName: string): void {
        this.setState({selectedItem: itemName});
        this.props?.onPathOver(itemName);
    }


    public render() {
        // Create the element
        const div = new ReactFauxDOM.Element("div");
        const data = this.props.data;
        let color = BarChart.BAR_COLOR;
        if (this.props.color && this.props.color != "") {
            color = this.props.color;
        }

        const margin = {top: 0, right: 20, bottom: 30, left: 40},
            width = this.props.width - margin.left - margin.right,
            height = this.props.height - margin.top - margin.bottom - 80;
        const barSpacePercentage = 0.35;
        const barWidth = (width / data.length) / (1 + barSpacePercentage);
        const barSpace = (width / data.length) * barSpacePercentage;
        // create svg for histogram.
        const gWidth = this.props.width;
        const svg = d3.select(div).append("svg")
            .attr("width", gWidth)
            .attr("height", this.props.height)
            .append("g")
            .attr("transform", `translate(${margin.left}, 40)`);

        svg.append("text")
            .text(this.props.yUnitOfMeasure)
            .attr("transform", `translate(-40, -30)`)
            .classed("chart__unit-of-measure", true);

        // create function for x-axis mapping.
        const x = d3.scaleBand()
            .rangeRound([0, width])
            .domain(data.map(function (d: BarChartRow) {
                    return d.name;
                }),
            );
        // Create function for y-axis map.
        const _maxData: number = d3.max(data, function (d: BarChartRow) {
            return d.value;
        })
        const y = d3.scaleLinear()
            .range([height, 0])
            .domain(
                [
                    0,
                    this.props.minYScaleValue && this.props.minYScaleValue > _maxData
                        ? this.props.minYScaleValue
                        : _maxData,
                ],
            );
        // Add x-axis to the histogram svg.
        svg.append("g")
            .attr("class", "x axis")
            .attr("transform", `translate(0,${height})`)
            .call((d3 as any).axisBottom().scale(x))
            .selectAll("text")
            .attr("data-id", function (d, i) {
                return "dDateText" + i;
            })
            .attr("class", "dateFieldsClass")
            .style("font-size", "12px")
            .style("fill", "black")
            .attr("y", 9)
            .attr("x", 3)
            .attr("dy", ".35em")
            .attr("transform", "rotate(35)")
            .style("text-anchor", "start");

        const yAxisLeft = (d3 as any).axisRight()
            .scale(y)
            .ticks([5])
            .tickSize(width);

        const customAxis = function (g) {
            g.selectAll("text")
                .attr("x", -30)
                .attr("dy", 4)
                .style("fill", "black");
        };

        if (this.props?.withYScaleValues ?? true) {
            svg.append("g")
                .attr("class", "y axis")
                .style("fill", "grey")
                .call(yAxisLeft)
                .call(customAxis);
        }

        svg.selectAll("g.y.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.x.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.y.axis").selectAll("line").style("display", "").style("stroke", "#c0c0c0");
        svg.selectAll("g.x.axis").selectAll("line").style("display", "").style("stroke", "black");
        const maxYValue: number = d3.max(data, function (d: BarChartRow) {
                return Math.max(d.value);
            }
        )
        y.domain([0,
            this.props.minYScaleValue && this.props.minYScaleValue > maxYValue
                ? this.props.minYScaleValue
                : maxYValue
        ]);

        // Create bars for histogram to contain rectangles and freq labels.
        const bars = svg.selectAll(".bar").data(data).enter().append("g").attr("class", "bar");

        let maxVal = 0
        data.forEach((elem) => {
            if (elem.value > maxVal) {
                maxVal = elem.value
            }
        })

        const zeroValue = (maxVal !== 0 ? maxVal : 1) * 0.008;
        // create the rectangles.
        bars.append("rect")
            .attr("x", function (d: BarChartRow) {
                return x(d.name) + barSpace / 2;
            })
            .attr("y", function (d: BarChartRow) {
                return y(d.value > zeroValue ? d.value : zeroValue);
            })
            .attr("width", barWidth)
            .attr("data-id", function (d: BarChartRow) {
                return MarketingTabMapper.codifyEntityName(d.name);
            })
            .attr("height", function (d: BarChartRow) {
                return height - y(d.value > zeroValue ? d.value : zeroValue);
            })
            .attr("fill", function (d: BarChartRow) {
                if (d.color && d.color != "") {
                    return d.color;
                }
                return color;
            })
            .style("opacity", ".8")
            .on("mouseover", (event, d: BarChartRow) => {
                this.setSelectedItem(d.name);
            })
            .on("mouseout", () => {
                this.setSelectedItem(null);
            })
            .style("cursor", "pointer");

        // Create the frequency labels above the rectangles.
        if (this.props?.withFrequencyField ?? true) {
            bars.append("text")
                .text(function (d: BarChartRow) {
                    return PrettyPrinter.salesQuantity(d.value);
                })
                .attr("data-id", function (d, i) {
                    return "frequencyField" + i;
                })
                .attr("x", function (d: BarChartRow) {
                    return x(d.name) + (barWidth * 0.5) + barSpace / 2; // + 3;
                })
                .attr("y", function (d: BarChartRow) {
                    return y(d.value > zeroValue ? d.value : zeroValue) - 5;
                })
                .attr("text-anchor", "middle")
                .attr("font-weight", "600");
        }

        if (this.props?.labelsOptions?.externalLegend) {
            if (this.props.selectedItem != null) {
                bars.selectAll("[data-id=" + MarketingTabMapper.codifyEntityName(this.props.selectedItem) + "]")
                    .style("fill", BarChart.DEFAULT_COLOR);
            }
        } else {
            if (this.state.selectedItem != null) {
                bars.selectAll("[data-id=" + MarketingTabMapper.codifyEntityName(this.state.selectedItem) + "]")
                    .style("fill", BarChart.DEFAULT_COLOR);
            }
        }


        const normalizeText = (text: string): string => {
            if (text.length > 19) {
                text = text.slice(0, 18) + "...";
            }
            return text;
        };
        svg.selectAll("g.x.axis").selectAll("text").each(function (d, i) {
            d3.select(this).text(normalizeText(d3.select(this).text()));
        });
        return div.toReact();
    }
}
